/**
 * Commissioning checklist hook.
 * - Client-side checks (1-5): evaluated from device + config data in real-time
 * - Server-side checks (6-10): fetched from backend RPC
 */
import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { GatewayDevice, GatewayDeviceConfig, CommissioningCheckResult } from '@/types/gateway';
import { computeGatewayCommissioningChecks, completeGatewayCommissioning } from '@/services/gateway';

function runClientChecks(
  device: GatewayDevice | null,
  configs: GatewayDeviceConfig[]
): CommissioningCheckResult[] {
  if (!device) return [];

  const heartbeatAge = device.last_heartbeat_at
    ? (Date.now() - new Date(device.last_heartbeat_at).getTime()) / 1000
    : Infinity;

  return [
    {
      name: 'Gateway Online',
      passed: heartbeatAge < 120,
      message: heartbeatAge < 120
        ? `Last heartbeat ${Math.round(heartbeatAge)}s ago`
        : 'No heartbeat in the last 2 minutes',
      critical: true,
      overrideable: false,
      source: 'client' as const,
    },
    {
      name: 'WiFi Adequate',
      passed: device.wifi_rssi != null && device.wifi_rssi > -75,
      message: device.wifi_rssi != null
        ? `Signal: ${device.wifi_rssi} dBm${device.wifi_rssi <= -75 ? ' (weak)' : ''}`
        : 'No WiFi data',
      critical: true,
      overrideable: false,
      source: 'client' as const,
    },
    {
      name: 'NTP Synced',
      passed: device.ntp_synced === true,
      message: device.ntp_synced ? 'Time synchronized' : 'Time not synchronized',
      critical: true,
      overrideable: false,
      source: 'client' as const,
    },
    {
      name: 'Devices Found',
      passed: configs.length > 0,
      message: configs.length > 0
        ? `${configs.length} device${configs.length !== 1 ? 's' : ''} discovered`
        : 'No devices discovered on bus',
      critical: true,
      overrideable: false,
      source: 'client' as const,
    },
    {
      name: 'Expected Device Count',
      passed:
        device.bus_devices_expected == null ||
        configs.length >= device.bus_devices_expected,
      message:
        device.bus_devices_expected == null
          ? 'Expected count not set (will use discovered count)'
          : configs.length >= device.bus_devices_expected
            ? `${configs.length}/${device.bus_devices_expected} devices found`
            : `Only ${configs.length}/${device.bus_devices_expected} devices found`,
      critical: false,
      overrideable: true,
      source: 'client' as const,
    },
  ];
}

export function useGatewayCommissioning(
  device: GatewayDevice | null,
  configs: GatewayDeviceConfig[]
) {
  const queryClient = useQueryClient();
  const deviceId = device?.id;

  const clientChecks = useMemo(
    () => runClientChecks(device, configs),
    [device, configs]
  );

  const serverChecksQuery = useQuery({
    queryKey: ['gateway-commissioning-checks', deviceId],
    queryFn: async () => {
      if (!deviceId) return [];
      return computeGatewayCommissioningChecks(deviceId);
    },
    enabled: Boolean(deviceId) && device?.lifecycle_status === 'commissioning',
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  const allChecks: CommissioningCheckResult[] = useMemo(
    () => [...clientChecks, ...(serverChecksQuery.data ?? [])],
    [clientChecks, serverChecksQuery.data]
  );

  const criticalsPassed = allChecks
    .filter((c) => c.critical)
    .every((c) => c.passed);

  const completeMutation = useMutation({
    mutationFn: async ({
      overrides,
      busDevicesExpected,
    }: {
      overrides: { check_name: string; note: string }[];
      busDevicesExpected: number;
    }) => {
      if (!deviceId) throw new Error('No device ID');
      return completeGatewayCommissioning({
        deviceId,
        overrides,
        busDevicesExpected,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateway-device', device?.property_id] });
      queryClient.invalidateQueries({ queryKey: ['gateway-commissioning-checks', deviceId] });
    },
  });

  return {
    checks: allChecks,
    clientChecks,
    serverChecksLoading: serverChecksQuery.isLoading,
    serverChecksError: serverChecksQuery.error as Error | null,
    criticalsPassed,
    completeCommissioning: completeMutation.mutateAsync,
    isCompleting: completeMutation.isPending,
    completeError: completeMutation.error as Error | null,
  };
}
